import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ProfileData, supabase, useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';

import { prepareImageForUpload } from 'libs/imageHelpers';

import { LeadData } from 'components/layout/PursuitApplication';
import { ProfileEditor } from 'components/layout/ProfileEditor';
import { ProfileDetails } from 'components/ui/ProfileDetails';
import { Loader } from 'components/ui/Loader';

const { REACT_APP_SUPABASE_URL } = process.env;

export interface PursuitProfileRawData {
    profile_image_url: string;
    location: string;
    birthday: string;
    company: string;
    headline: string;
    instagram: string;
    zone_of_genius: string;
    interests: string;
    children: string;
    is_published?: boolean;
    has_completed_pursuit_onboarding?: boolean;
}

interface ProfileContainerProps {
    profileId?: string;
    isProfileCreation?: boolean;
    onToggleEdit?: () => void;
}

export const ProfileContainer = ({
    isProfileCreation = false,
    profileId,
    onToggleEdit,
}: ProfileContainerProps) => {
    const { userProfile, userId, userEmail, getProfiles } = useAuth();
    const { showSuccessToast, showErrorToast } = useToast();

    const [lead, setLead] = useState<LeadData | null>(null);
    const [profile, setProfile] = useState<ProfileData | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        const getProfile = async () => {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', profileId ?? '')
                .maybeSingle();

            if (error == null && data != null) {
                setProfile(data);
            } else {
                showErrorToast('Unable to fetch profile data');
            }
        };

        if (
            userId !== '' &&
            profileId != null &&
            profileId !== '' &&
            profileId !== userId
        ) {
            getProfile();
        }
    }, [profileId, showErrorToast, userId]);

    const getLead = useCallback(async () => {
        const { data, error } = await supabase
            .from('leads')
            .select('*')
            .eq('profile_id', userId)
            .maybeSingle();

        if (error == null && data != null) {
            setLead(data);
        } else {
            showErrorToast(error?.message ?? '');
            showErrorToast('Unable to fetch application data');
        }
    }, [showErrorToast, userId]);

    useEffect(() => {
        if (userId !== '') {
            getLead();
        }
    }, [userId, getLead]);

    const parsedLeadMetadata = useMemo(
        () => JSON.parse(JSON.stringify(lead?.metadata ?? {})),
        [lead?.metadata]
    );

    const onSaveLead = useCallback(
        async (phoneNumber: string) => {
            const { error } = await supabase
                .from('leads')
                .update({
                    metadata: {
                        ...parsedLeadMetadata,
                        phone_number: phoneNumber,
                    },
                })
                .eq('profile_id', userId);

            if (error == null) {
                await getLead();
            } else {
                showErrorToast(error.message);
            }
        },
        [getLead, parsedLeadMetadata, showErrorToast, userId]
    );

    const parsedProfileRawData = useMemo(
        () => JSON.parse(JSON.stringify(userProfile?.raw_data ?? {})),
        [userProfile?.raw_data]
    );

    const onSaveProfileImage = useCallback(
        async (file: File) => {
            try {
                const { data: sessionData } = await supabase.auth.getSession();
                const token = sessionData.session?.access_token;
                if (!token) {
                    showErrorToast('Please sign in first.');
                    return null;
                }

                const safeFile = await prepareImageForUpload(file);
                const form = new FormData();
                form.append('file', safeFile);
                form.append('category', 'profile');

                const res = await fetch(
                    `${REACT_APP_SUPABASE_URL}/functions/v1/upload-image`,
                    {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                        body: form,
                    }
                );

                const json = await res.json();
                if (!res.ok) {
                    console.error(json);
                    showErrorToast(json.error || 'Upload failed');
                    return null;
                }
                return json as { publicUrl: string };
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (e: any) {
                showErrorToast(
                    e?.message ?? 'Unexpected error uploading image'
                );
                return null;
            }
        },
        [showErrorToast]
    );

    const onSaveProfile = useCallback(
        async (
            fullName: string,
            linkedIn: string,
            pursuitProfileRawData: PursuitProfileRawData
        ) => {
            const { error } = await supabase
                .from('profiles')
                .update({
                    full_name: fullName,
                    linkedin: linkedIn,
                    raw_data: {
                        ...parsedProfileRawData,
                        ...pursuitProfileRawData,
                        is_published: true,
                    },
                })
                .eq('id', userId);

            if (error == null) {
                showSuccessToast('Your profile has been published!');
                await getProfiles?.();
                return null;
            } else {
                showErrorToast(error.message);
                return error;
            }
        },
        [
            getProfiles,
            parsedProfileRawData,
            showErrorToast,
            showSuccessToast,
            userId,
        ]
    );

    useEffect(() => {
        if (isEditing || !isEditing) {
            onToggleEdit?.();
        }
    }, [isEditing, onToggleEdit]);

    const onCreateIntake = useCallback(
        async (other: string) => {
            const { error } = await supabase.from('intakes').insert({
                created_by: userId,
                email: userEmail,
                other,
            });

            if (error != null) {
                showErrorToast(error.message);
                throw error;
            }
        },
        [showErrorToast, userEmail, userId]
    );

    const onSaveAndPublish = useCallback(
        async (
            phoneNumber: string,
            fullName: string,
            linkedIn: string,
            pursuitProfileRawData: PursuitProfileRawData,
            selectedFile: File | null,
            other: string
        ) => {
            setIsSaving(true);
            if (
                isProfileCreation &&
                (userProfile?.type === 'Pursuit Retreat' ||
                    userProfile?.type === 'Pursuit Digital')
            ) {
                await onCreateIntake(other);
            }
            await onSaveLead(phoneNumber);
            const finalRawData = { ...pursuitProfileRawData };
            if (selectedFile != null) {
                const upload = await onSaveProfileImage(selectedFile);
                if (!upload?.publicUrl) {
                    throw new Error('Unable to upload image');
                }
                finalRawData.profile_image_url = upload.publicUrl;
            }
            await onSaveProfile(fullName, linkedIn, finalRawData);
            setIsEditing(false);
            setIsSaving(false);
        },
        [
            isProfileCreation,
            onCreateIntake,
            onSaveLead,
            onSaveProfile,
            onSaveProfileImage,
            userProfile?.type,
        ]
    );

    return userProfile == null || lead == null ? (
        <Loader />
    ) : isEditing || isProfileCreation ? (
        <ProfileEditor
            userProfile={userProfile}
            leadData={lead}
            parsedProfileRawData={parsedProfileRawData}
            parsedLeadMetadata={parsedLeadMetadata}
            onCancel={() => setIsEditing(false)}
            onSaveAndPublish={onSaveAndPublish}
            isSaving={isSaving}
            isProfileCreation={isProfileCreation}
        />
    ) : (
        <ProfileDetails
            profile={profile || userProfile}
            isCurrentUser={profileId === userId}
            onEdit={() => setIsEditing(true)}
        />
    );
};
